import React, {useCallback, useEffect, useRef, useMemo} from 'react';
import DetailNavTitle from '@businessComponents/detail-nav-title';
import checkCoins from '@/assets/imgs/check/check-coins.png';
import lockIcon from '@/assets/imgs/check/lock.webp';
import {goBack, goTo} from '@utils';
import VipHalfCard from '@/common-pages/vip/vip-half-card';
import {Image} from 'antd-mobile';
import {cn} from '@utils/utils';
import {toPriceStr} from '@components/utils';
import {useModalManager} from '@basicComponents/sequential-modal/ModalManagerProvider';
import CheckSuccess from '@/common-pages/check-in/check-success';
import CheckInButton from './check-in-button';
import {CheckInStatus} from './types';
import {useCheckInData} from '@/common-pages/hooks/useCheckInData';
import {rules} from './check-in.service';
import globalStore from '@services/global.state';
import TaskItem from './task-item';
import {homePage} from '@/config';

let giftIcon = require('@/assets/imgs/check/gift.webp');

const rechargeIcon = require('@assets/imgs/check/rechargeIcon.svg').default;
const betIcon = require('@assets/imgs/check/betIcon.svg').default;

const LockOverlay = ({icon, className}: {icon: string; className?: string}) => (
  <div
    className={cn(
      'absolute inset-0 bg-black/30 rounded-[0.25rem_0.25rem_0.5rem_0.5rem] flex items-end justify-center pb-[0.9375rem]',
      className,
    )}>
    <div
      className="w-6 h-8 bg-center bg-no-repeat bg-contain"
      style={{backgroundImage: `url(${icon})`}}
    />
  </div>
);

export default function CheckIn() {
  const vipCardRef = useRef<{init: () => void}>(null);
  const {showModal} = useModalManager();
  const taskContainerRef = useRef<HTMLDivElement>(null);
  // 使用签到数据 hook
  const {
    renderList,
    checkIn,
    receiveReward,
    receiveExtraReward,
    isCheckingIn,
    isReceivingReward,
    isReceivingExtraReward,
    rawData,
    currentDayTasks,
    currentWeekDay,
    getStatusCount,
    extraRewardReceived,
  } = useCheckInData();

  // 任务配置
  const taskConfigs = useMemo(() => {
    const configs = [];
    if (currentDayTasks) {
      if (currentDayTasks.depositTask > 0) {
        configs.push({
          type: 'deposit',
          icon: rechargeIcon,
          label: 'Recharge',
          completed: currentDayTasks.depositTaskCompleted,
          target: currentDayTasks.depositTask,
          buttonText: 'Complete Now',
          iconClassName: 'w-3 h-[0.8125rem]',
          onButtonClick: () => {
            // 跳转到充值页面
            goTo('Recharge');
          },
        });
      }
      if (currentDayTasks.betTask > 0) {
        configs.push({
          type: 'bet',
          icon: betIcon,
          label: 'bet',
          completed: currentDayTasks.betTaskCompleted,
          target: currentDayTasks.betTask,
          buttonText: 'Complete Now',
          iconClassName: 'w-[1.125rem] h-[1.125rem]',
          onButtonClick: () => {
            // 跳转到首页
            goTo(homePage, {screen: 'Home'});
          },
        });
      }
    }
    return configs;
  }, [currentDayTasks]);

  const updateVipCard = useCallback(() => {
    vipCardRef.current?.init();
  }, []);
  useEffect(() => {
    updateVipCard();
  }, [updateVipCard]);

  // 处理签到点击
  const handleCheckIn = async () => {
    try {
      const res = await checkIn();
      if (res.autoRewardAmount && res.autoRewardAmount > 0) {
        // 打开奖励弹窗
        showSuccess(res.autoRewardAmount);
      }
    } catch (error) {
      globalStore.globalWaringTotal(
        'Check-in failed, the event hasn’t started yet.',
      );
    }
  };

  // 处理领取奖励点击（支持每日奖励和宝箱奖励）
  const handleReceiveReward = async (isExtraReward: boolean = false) => {
    try {
      // 根据类型调用不同的接口
      const result = isExtraReward
        ? await receiveExtraReward()
        : await receiveReward();

      if (result.rewardAmount && result.rewardAmount > 0) {
        showSuccess(result.rewardAmount);
      }
    } catch (error) {
      console.error(`领取${isExtraReward ? '额外' : '每日'}奖励失败:`, error);
      // 显示错误提示
      if (isExtraReward) {
        globalStore.globalWaringTotal(
          'Your extra reward isn’t ready yet. Complete today’s tasks to claim it.',
        );
        if (taskContainerRef.current) {
          taskContainerRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        }
      } else {
        globalStore.globalWaringTotal(
          'Please complete the tasks before claiming rewards',
        );
        // 滚动到任务容器位置
        if (taskContainerRef.current) {
          taskContainerRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        }
      }
    }
  };

  const renderDay = (item: any, i: number) => {
    const isLast = i === 6;
    // 判断当前项是否是正在操作的项
    const isCurrentDay = item.dayOfWeek === currentWeekDay;
    const showCheckingIn = isCurrentDay && isCheckingIn;
    const showReceivingReward = isCurrentDay && isReceivingReward;

    // 如果是第7天
    if (isLast) {
      // 如果7天全部签到完成，显示超级礼包UI
      if (getStatusCount(CheckInStatus.COMPLETED) >= 7) {
        // 根据是否已领取额外奖励来决定状态
        const chestStatus = extraRewardReceived
          ? CheckInStatus.COMPLETED
          : CheckInStatus.TASK_COMPLETED;

        return (
          <div key={i} className="col-span-3">
            <div
              className={cn(
                'text-t2 text-[0.625rem] capitalize font-black uppercase flex items-center justify-center border border-white/50 rounded-t-lg h-[1.5625rem]',
                'bg-[linear-gradient(180deg,#FFCB67_0%,#FFAF13_100%)]',
              )}>
              7 day Super Gift Pack
            </div>
            <div className="flex gap-4 py-3 px-4 rounded-[0.25rem_0.25rem_0.5rem_0.5rem] border border-white/70 w-full relative overflow-hidden -top-0.5 bg-[#FFE8BA]">
              <Image src={giftIcon} className="w-[3.625rem] h-12" />
              <div className="flex-1">
                <div className="text-t3 text-[0.5625rem] font-bold text-center mb-1">
                  A Super Gift Pack Awaits You to Unlock
                </div>
                <CheckInButton
                  status={chestStatus}
                  isReceivingReward={isReceivingExtraReward}
                  onClaimReward={() => handleReceiveReward(true)}
                  onCheckIn={handleCheckIn}
                />
              </div>
            </div>
          </div>
        );
      }
      // 如果是第7天但未全部签到，显示UI2
      return (
        <div key={i} className="col-span-3 relative">
          <div
            className={cn(
              'text-t2 text-[0.625rem] capitalize font-black uppercase flex items-center justify-center border border-white/50 rounded-t-lg h-[1.5625rem]',
              'bg-[linear-gradient(180deg,#FFCB67_0%,#FFAF13_100%)]',
            )}>
            SUNDAY
          </div>
          <div className="flex gap-4 py-3 px-4 rounded-[0.25rem_0.25rem_0.5rem_0.5rem] border border-white/70 w-full relative overflow-hidden -top-0.5 bg-[#FFE8BA]">
            <Image src={item.icon} className="w-[3.625rem] h-12" />
            <div className="flex-1">
              <div
                className={cn(
                  'text-base text-center font-black text-[#F15802]',
                )}>
                {toPriceStr(item.rewardAmount, {fixed: 0})}
              </div>
              <CheckInButton
                status={item.status}
                isCheckingIn={showCheckingIn}
                isReceivingReward={showReceivingReward}
                onCheckIn={handleCheckIn}
                onClaimReward={() => handleReceiveReward(false)}
              />
            </div>
          </div>
          {item.status === CheckInStatus.LOCKED && (
            <LockOverlay icon={lockIcon} />
          )}
        </div>
      );
    }

    return (
      <div key={i} className={cn('relative', isLast && 'col-span-3')}>
        <div
          className={cn(
            'text-t2 text-[0.625rem] font-black uppercase flex items-center justify-center border border-white/50 rounded-t-lg h-[1.5625rem]',
            item.status === CheckInStatus.AVAILABLE ||
              item.status === CheckInStatus.LOCKED
              ? 'bg-[linear-gradient(180deg,#FFCB67_0%,#FFAF13_100%)]'
              : 'bg-[linear-gradient(180deg,#d3d3d3_0%,#bababa_100%)]',
          )}>
          {item.day}
        </div>
        <div
          className={cn(
            'rounded-[0.25rem_0.25rem_0.5rem_0.5rem] border border-white/70 pt-8 w-full py-1 relative overflow-hidden -top-0.5',
            item.status === CheckInStatus.AVAILABLE ||
              item.status === CheckInStatus.LOCKED
              ? 'bg-[#FFE8BA]'
              : 'bg-[#E3E3E3]',
          )}>
          <Image
            className="absolute w-8 h-6 left-1/2 -translate-x-1/2 top-3"
            src={item.icon}
          />
          <div
            className={cn(
              'text-base text-center font-black mt-[2.5rem]',
              item.status === CheckInStatus.AVAILABLE ||
                item.status === CheckInStatus.LOCKED
                ? 'text-[#F15802]'
                : 'text-[#5f6975]',
            )}>
            {toPriceStr(item.rewardAmount, {fixed: 0})}
          </div>
          <div className="p-3">
            <CheckInButton
              status={item.status}
              isCheckingIn={showCheckingIn}
              isReceivingReward={showReceivingReward}
              onCheckIn={handleCheckIn}
              onClaimReward={() => handleReceiveReward(false)}
            />
          </div>
        </div>
        {item.status === CheckInStatus.LOCKED && (
          <LockOverlay icon={lockIcon} />
        )}
      </div>
    );
  };
  const showSuccess = (value: number) => {
    showModal({
      id: CheckSuccess.MODAL_ID,
      modals: [<CheckSuccess value={value} />],
    });
  };
  return (
    <div className="bg-[#AA3726] relative h-screen flex flex-col overflow-hidden translate-x-0">
      <DetailNavTitle
        title="check-in"
        serverRight
        hideAmount
        onBack={() => {
          goBack();
        }}
      />
      <div className="flex-1 overflow-y-auto pb-8 pt-[7.75rem] relative">
        <div className="h-[9.25rem] absolute top-0 w-full bg-[linear-gradient(277deg,#FFD67E_-10%,#FF61A5_30.42%,#E3452E_88.65%)] capitalize px-4 pt-8">
          <div className="absolute bottom-0 left-0 right-0 h-[5.375rem] bg-[linear-gradient(180deg,rgba(165,35,16,0)_0%,#AA3726_100%)]" />
          <div className="relative z-10">
            <div className="text-[1.75rem] font-black text-white">
              7-Day Check-in
            </div>
            <div className="text-sm font-bold text-white/80">
              Check in consecutively to get
            </div>
            <div className="text-sm font-bold text-white/80">
              wonderful gifts
            </div>
          </div>
          <div
            className="size-[13.125rem] bg-full absolute -right-3 -top-9 z-1"
            style={{
              backgroundImage: `url(${checkCoins})`,
            }}
          />
        </div>
        <div className="px-4">
          <VipHalfCard
            ref={vipCardRef}
            className="rounded-xl h-[6rem]"
            renderRedPoint={() => (
              <div className="flex items-center gap-1">
                <span className="text-t1 text-[0.625rem] font-medium opacity-50">
                  VIP Bonus:
                </span>
                <span className="text-[#f15802] text-xs font-black tracking-[-0.01875rem]">
                  X{(rawData?.currentVipBonusRate || 0) * 100}%
                </span>
              </div>
            )}
            login
            renderBottomAction={() => {
              return (
                <div className="text-t1 font-medium opacity-50 text-[0.625rem]">
                  The higher your level, the more generous the rewards
                </div>
              );
            }}
          />
          <div className="rounded-xl border border-white/70 bg-[linear-gradient(180deg,#FFECD2_0%,#FFCE8E_100%)] mt-2 p-3">
            <div className="text-sm font-bold text-t1">
              Checked in for{' '}
              <span className="text-[#F00] font-black text-base">
                {getStatusCount(CheckInStatus.COMPLETED)}
              </span>{' '}
              days
            </div>
            <div className="mt-3">
              <div className="grid grid-cols-3 gap-1">
                {renderList.map(renderDay)}
              </div>
            </div>
          </div>
          <div ref={taskContainerRef}>
            {taskConfigs.length > 0 && (
              <div className="rounded-xl border border-white/70 bg-gradient-to-b from-[#FFECD2] to-[#FFEACD] px-4 py-3 flex flex-col gap-3 mt-2">
                {/* Title */}
                <div className="flex gap-2 items-center">
                  <div className="bg-[#00B512] w-1 h-3 shrink-0" />
                  <p className="capitalize font-bold text-sm text-t1">
                    Today's Tasks
                  </p>
                </div>

                {/* Render tasks using TaskItem component */}
                {taskConfigs.map(task => (
                  <TaskItem
                    key={task.type}
                    icon={task.icon}
                    label={task.label}
                    completed={task.completed}
                    target={task.target}
                    buttonText={task.buttonText}
                    iconClassName={task.iconClassName}
                    onButtonClick={task.onButtonClick}
                  />
                ))}
              </div>
            )}
          </div>
          <div className="rounded-xl border border-white/70 bg-gradient-to-b from-[#FFECD2] to-[#FFEACD] px-4 py-3 flex flex-col gap-3 mt-2">
            {/* Title */}
            <div className="flex gap-2 items-center">
              <div className="bg-[#00B512] w-1 h-3 shrink-0" />
              <p className="capitalize font-bold text-sm text-t1">
                Event Rules
              </p>
            </div>
            <ul className="flex flex-col gap-2 pl-4">
              {rules.map(item => {
                return (
                  <li className="text-xs text-t2 font-medium list-disc">
                    {item}
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
